import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Save, Loader2, Download, Database, Type, Palette, Eye, History, Trash2, FileArchive, Plus, X, Upload, Image, Server, Lock, ShieldCheck, Map, Check, Code, ToggleLeft, ToggleRight, Copy, ExternalLink } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { API } from '../App';

function formatBytes(b) {
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
function formatDate(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TILE_STYLES = [
  { key: 'osm', name: 'OpenStreetMap', note: 'Standard — kostenlos, kein API-Key', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', thumb: 'https://a.tile.openstreetmap.org/12/2200/1343.png' },
  { key: 'carto_voyager', name: 'Carto Voyager', note: 'Modern, farbenfroh, Google-Maps-ähnlich', url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', thumb: 'https://a.basemaps.cartocdn.com/rastertiles/voyager/12/2200/1343.png', needsKey: true, apiUrl: 'https://carto.com/pricing/', provider: 'CARTO' },
  { key: 'carto_positron', name: 'Carto Positron', note: 'Minimalistisch — Pins stechen hervor', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', thumb: 'https://a.basemaps.cartocdn.com/light_all/12/2200/1343.png', needsKey: true, apiUrl: 'https://carto.com/pricing/', provider: 'CARTO' },
  { key: 'carto_dark', name: 'Carto Dark Matter', note: 'Dunkel — Pins leuchten intensiv', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', thumb: 'https://a.basemaps.cartocdn.com/dark_all/12/2200/1343.png', needsKey: true, apiUrl: 'https://carto.com/pricing/', provider: 'CARTO' },
  { key: 'opentopomap', name: 'OpenTopoMap', note: 'Topographisch — kostenlos, kein API-Key', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', thumb: 'https://a.tile.opentopomap.org/12/2200/1343.png' },
  { key: 'carto_voyager_under', name: 'Voyager Labels Under', note: 'Wie Voyager, Labels unter Kartenebenen', url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png', thumb: 'https://a.basemaps.cartocdn.com/rastertiles/voyager_labels_under/12/2200/1343.png', needsKey: true, apiUrl: 'https://carto.com/pricing/', provider: 'CARTO' },
];

export default function HerzgruppensucheSettingsPage() {
  const { apiFetch } = useApi();
  const [cfg, setCfg] = useState({
    page_title: '', heading: '', description: '',
    label_location: '', label_radius: '', label_offer: '',
    placeholder_location: '', button_search: '',
    results_text: '', no_results: '',
    footer_links_html: '', footer_copyright: '',
    detail_back_button: '', detail_contact_heading: '',
    detail_offers_heading: '', detail_print_button: '',
    primary_color: '#E6007E', secondary_color: '#3498db',
    map_tile_style: 'osm',
    tile_api_key: '',
    logo_max_width: 800,
    widget_base_url: '',
    widget_show_logo: true,
    widget_show_footer: true,
    widget_heading_color: '#2c3e50',
    widget_text_color: '#555555',
  });
  const [dbCreds, setDbCreds] = useState(null);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [versionNote, setVersionNote] = useState('');

  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // FTP state
  const [ftpCfg, setFtpCfg] = useState({ protocol: 'sftp', host: '', port: '22', user: '', password: '', remote_path: '/' });
  const [ftpPasswordSet, setFtpPasswordSet] = useState(false);
  const [ftpSaving, setFtpSaving] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(null);

  // Widget state
  const [showWidgetPreview, setShowWidgetPreview] = useState(false);
  const [widgetLivePreview, setWidgetLivePreview] = useState(false);
  const [widgetPreviewKey, setWidgetPreviewKey] = useState(0);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [widgetSaving, setWidgetSaving] = useState(false);

  const loadVersions = useCallback(() => {
    apiFetch('/api/herzgruppensuche/versions').then(r => r?.json()).then(d => {
      if (Array.isArray(d)) setVersions(d);
    }).catch(() => {});
  }, [apiFetch]);

  const loadLogo = useCallback(() => {
    const ts = Date.now();
    setLogoUrl(`${API}/api/herzgruppensuche/logo?_t=${ts}`);
  }, []);

  const loadFtpConfig = useCallback(() => {
    apiFetch('/api/settings/ftp-config').then(r => r?.json()).then(d => {
      if (d) {
        setFtpCfg(prev => ({ ...prev, protocol: d.protocol || 'sftp', host: d.host || '', port: d.port || '22', user: d.user || '', remote_path: d.remote_path || '/', password: '' }));
        setFtpPasswordSet(!!d.password_set);
      }
    }).catch(() => {});
  }, [apiFetch]);

  useEffect(() => {
    apiFetch('/api/settings/search-page-config').then(r => r?.json()).then(d => {
      if (d) setCfg(prev => ({ ...prev, ...d }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
    apiFetch('/api/herzgruppensuche/db-credentials').then(r => r?.json()).then(d => {
      if (d) setDbCreds(d);
    }).catch(() => {});
    loadVersions();
    loadLogo();
    loadFtpConfig();
  }, [apiFetch, loadVersions, loadLogo, loadFtpConfig]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };
  const set = (k) => (e) => setCfg(prev => ({ ...prev, [k]: e.target.value }));
  const setFtp = (k) => (e) => setFtpCfg(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings/search-page-config', {
        method: 'PUT', body: JSON.stringify(cfg),
      });
      const d = await res.json();
      flash(d.message || 'Gespeichert');
    } catch { flash('Fehler beim Speichern'); }
    setSaving(false);
  };

  const handleCreateVersion = async () => {
    setCreating(true);
    try {
      await apiFetch('/api/settings/search-page-config', { method: 'PUT', body: JSON.stringify(cfg) });
      const res = await apiFetch('/api/herzgruppensuche/versions', {
        method: 'POST', body: JSON.stringify({ note: versionNote }),
      });
      const d = await res.json();
      flash(d.message || 'Version erstellt');
      setVersionNote('');
      loadVersions();
      if (d.version?.version) {
        downloadVersion(d.version.version, d.version.filename);
      }
    } catch { flash('Fehler beim Erstellen'); }
    setCreating(false);
  };

  const downloadVersion = (vNum, filename) => {
    const token = localStorage.getItem('bgpr_token');
    fetch(`${API}/api/herzgruppensuche/versions/${vNum}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const deleteVersion = async (vNum) => {
    if (!window.confirm(`Version ${vNum} wirklich löschen?`)) return;
    try {
      await apiFetch(`/api/herzgruppensuche/versions/${vNum}`, { method: 'DELETE' });
      flash(`Version ${vNum} gelöscht`);
      loadVersions();
    } catch { flash('Fehler beim Löschen'); }
  };

  // Logo handlers
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('bgpr_token');
      const res = await fetch(`${API}/api/herzgruppensuche/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const d = await res.json();
      if (d.status) {
        flash('Logo hochgeladen');
        loadLogo();
      } else {
        flash(d.detail || 'Fehler beim Hochladen');
      }
    } catch { flash('Fehler beim Hochladen'); }
    setLogoUploading(false);
    e.target.value = '';
  };

  const handleLogoDelete = async () => {
    try {
      await apiFetch('/api/herzgruppensuche/logo', { method: 'DELETE' });
      flash('Custom-Logo entfernt');
      loadLogo();
    } catch { flash('Fehler'); }
  };

  // FTP handlers
  const handleFtpSave = async () => {
    setFtpSaving(true);
    try {
      const res = await apiFetch('/api/settings/ftp-config', {
        method: 'PUT', body: JSON.stringify(ftpCfg),
      });
      const d = await res.json();
      flash(d.message || 'FTP-Konfiguration gespeichert');
      loadFtpConfig();
    } catch { flash('Fehler beim Speichern der FTP-Daten'); }
    setFtpSaving(false);
  };

  const handleFtpUpload = async (vNum) => {
    setUploadingVersion(vNum);
    try {
      const res = await apiFetch(`/api/herzgruppensuche/ftp-upload/${vNum}`, { method: 'POST' });
      const d = await res.json();
      if (d.status) {
        flash(d.message || `Version ${vNum} hochgeladen`);
      } else {
        flash(d.detail || 'Upload fehlgeschlagen');
      }
    } catch (err) {
      flash('FTP-Upload fehlgeschlagen');
    }
    setUploadingVersion(null);
  };

  const previewUrl = `${API}/api/herzgruppensuche/preview?token=${localStorage.getItem('bgpr_token')}`;
  const widgetPreviewUrl = `${API}/api/herzgruppensuche/widget-preview?token=${localStorage.getItem('bgpr_token')}`;

  const widgetBaseUrl = (cfg.widget_base_url || '').replace(/\/+$/, '');
  const embedCode = `<script src="${widgetBaseUrl}/widget.js"><\/script>\n<div id="bgpr-herzgruppensuche"></div>`;
  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    });
  };

  const handleWidgetSave = async () => {
    setWidgetSaving(true);
    try {
      const res = await apiFetch('/api/settings/search-page-config', {
        method: 'PUT', body: JSON.stringify(cfg),
      });
      const d = await res.json();
      flash(d.message || 'Widget-Einstellungen gespeichert');
      // Refresh live preview if open
      if (widgetLivePreview) {
        setWidgetPreviewKey(k => k + 1);
      }
    } catch { flash('Fehler beim Speichern'); }
    setWidgetSaving(false);
  };

  return (
    <div data-testid="herzgruppensuche-settings">
      <div className="page-header">
        <h2>Herzgruppensuche extern</h2>
        <p>PHP-Suchseite konfigurieren, Vorschau anzeigen und als versionierte ZIP herunterladen</p>
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 8, background: '#ECFDF5', color: '#065F46', fontSize: 13, fontWeight: 600 }} data-testid="hgs-msg">
          {msg}
        </div>
      )}

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowPreview(false)}>
          <div style={{ width: '90vw', height: '85vh', background: '#fff', borderRadius: 12, overflow: 'hidden', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #eee', background: '#f9fafb' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Vorschau — Herzgruppensuche</span>
              <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} data-testid="preview-close"><X size={20} /></button>
            </div>
            <iframe src={previewUrl} title="Herzgruppensuche Vorschau" style={{ width: '100%', height: 'calc(100% - 46px)', border: 'none' }} data-testid="preview-iframe" />
          </div>
        </div>
      )}

      {/* ── DB-Zugangsdaten ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-db-section">
        <div className="card-header">
          <h3>
            <span className="card-header-icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}><Database size={18} /></span>
            Datenbank-Verbindung
          </h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Die PHP-Suchseite verbindet sich direkt mit der gleichen MariaDB. Zugangsdaten werden automatisch in die ZIP eingebettet.
          </p>
          {dbCreds && (
            <div style={{ background: 'var(--page-bg)', borderRadius: 8, padding: '12px 16px', fontFamily: 'monospace', fontSize: 13, lineHeight: 2 }}>
              <div><strong>Host:</strong> {dbCreds.host}</div>
              <div><strong>Port:</strong> {dbCreds.port}</div>
              <div><strong>Benutzer:</strong> {dbCreds.user}</div>
              <div><strong>Datenbank:</strong> {dbCreds.database}</div>
              <div><strong>Passwort:</strong> {dbCreds.password_set ? '••••••••••' : '(nicht gesetzt)'}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Logo ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-logo-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(236,72,153,0.1)', color: '#EC4899' }}><Image size={18} /></span> Logo</h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Das Logo wird oben rechts auf der Suchseite und der Detailseite angezeigt. Sie können ein eigenes Logo hochladen (SVG, PNG, JPG, WebP) oder das Standard-BGPR-Logo verwenden.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ background: '#f9fafb', borderRadius: 8, border: '1px solid #eee', padding: 16, minWidth: 200, textAlign: 'center' }}>
              {logoUrl && <img src={logoUrl} alt="Logo" style={{ maxWidth: cfg.logo_max_width || 800, maxHeight: 160 }} onError={e => { e.target.style.display = 'none'; }} data-testid="logo-preview" />}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} data-testid="logo-upload-btn">
                {logoUploading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
                {logoUploading ? 'Hochladen...' : 'Logo hochladen'}
                <input type="file" accept=".svg,.png,.jpg,.jpeg,.webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
              </label>
              <button className="btn" style={{ padding: '6px 14px', fontSize: 12, color: '#EF4444', border: '1px solid #FCA5A5', background: '#FEF2F2', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={handleLogoDelete} data-testid="logo-delete-btn">
                <Trash2 size={12} /> Custom-Logo entfernen
              </button>
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Logo-Größe: {cfg.logo_max_width || 800}px</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>200</span>
              <input type="range" min="200" max="1200" step="50" value={cfg.logo_max_width || 800}
                onChange={e => setCfg(prev => ({ ...prev, logo_max_width: parseInt(e.target.value) }))}
                style={{ flex: 1, accentColor: '#E6007E' }} data-testid="logo-size-slider" />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>1200</span>
            </div>
          </div>
          <div style={{ marginTop: 16, background: '#f9fafb', borderRadius: 8, border: '1px dashed #d1d5db', padding: 20, overflow: 'hidden', transition: 'all 0.2s' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, textAlign: 'right' }}>Vorschau bei {cfg.logo_max_width || 800}px</div>
            {logoUrl && <img src={logoUrl} alt="Logo Vorschau" style={{ maxWidth: cfg.logo_max_width || 800, maxHeight: 160, transition: 'max-width 0.2s' }} onError={e => { e.target.style.display = 'none'; }} />}
          </div>
        </div>
      </div>

      {/* ── Farben ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-colors-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(236,72,153,0.1)', color: '#EC4899' }}><Palette size={18} /></span> Farben</h3>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label>Primärfarbe (Links, Buttons)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={cfg.primary_color} onChange={set('primary_color')} style={{ width: 40, height: 36, padding: 0, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }} data-testid="input-primary-color" />
                <input className="form-input" value={cfg.primary_color} onChange={set('primary_color')} style={{ maxWidth: 120 }} />
              </div>
            </div>
            <div className="form-group">
              <label>Sekundärfarbe (Buttons, Hover)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={cfg.secondary_color} onChange={set('secondary_color')} style={{ width: 40, height: 36, padding: 0, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }} data-testid="input-secondary-color" />
                <input className="form-input" value={cfg.secondary_color} onChange={set('secondary_color')} style={{ maxWidth: 120 }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Texte Suchseite ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-map-style-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)' }}><Map size={18} /></span> Kartenstil</h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Wählen Sie den Kartenstil für die Herzgruppensuche. Einige Anbieter erfordern einen API-Key.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
            {TILE_STYLES.map(s => {
              const selected = cfg.map_tile_style === s.key;
              const needsKey = s.needsKey && !cfg.tile_api_key;
              return (
                <div key={s.key}
                  onClick={() => setCfg(prev => ({ ...prev, map_tile_style: s.key }))}
                  style={{
                    cursor: 'pointer', borderRadius: 10, overflow: 'hidden',
                    border: selected ? '2px solid #E6007E' : '2px solid #e5e7eb',
                    boxShadow: selected ? '0 0 0 3px rgba(230,0,126,0.15)' : 'none',
                    transition: 'all 0.2s',
                  }}
                  data-testid={`tile-style-${s.key}`}
                >
                  <div style={{ height: 120, overflow: 'hidden', position: 'relative', background: '#f0f0f0' }}>
                    <img src={s.thumb} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                    {selected && (
                      <div style={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: '50%', background: '#E6007E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Check size={14} color="#fff" />
                      </div>
                    )}
                    {s.needsKey && (
                      <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6 }}>
                        <Lock size={10} /> API-Key
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{s.name}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{s.note}</div>
                    {s.apiUrl && (
                      <a href={s.apiUrl} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 11, color: '#3B82F6', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 4 }}>
                        <ExternalLink size={10} /> API-Key bei {s.provider} besorgen
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* API Key input - shown when a style requiring a key is selected */}
          {(() => {
            const selectedStyle = TILE_STYLES.find(s => s.key === cfg.map_tile_style);
            if (!selectedStyle?.needsKey) return null;
            return (
              <div style={{ marginTop: 20, padding: 16, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10 }}
                data-testid="tile-api-key-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Lock size={16} color="#D97706" />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>
                    {selectedStyle.provider} API-Key erforderlich
                  </span>
                </div>
                <p style={{ fontSize: 12, color: '#78716C', lineHeight: 1.5, margin: '0 0 12px' }}>
                  Dieser Kartenstil erfordert einen gültigen API-Key von {selectedStyle.provider}. Ohne API-Key wird automatisch OpenStreetMap als Fallback verwendet.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder={`${selectedStyle.provider} API-Key eingeben`}
                    value={cfg.tile_api_key || ''}
                    onChange={e => setCfg(prev => ({ ...prev, tile_api_key: e.target.value.trim() }))}
                    style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, outline: 'none', fontFamily: 'monospace' }}
                    data-testid="tile-api-key-input"
                  />
                  <a href={selectedStyle.apiUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', fontSize: 12, fontWeight: 500, color: '#3B82F6', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    <ExternalLink size={12} /> Key besorgen
                  </a>
                </div>
                {cfg.tile_api_key && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <ShieldCheck size={14} color="#059669" />
                    <span style={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>API-Key hinterlegt</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Texte Suchseite (Original) ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-texts-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)' }}><Type size={18} /></span> Texte der Suchseite</h3>
        </div>
        <div className="card-body">
          {!loaded ? <p>Laden...</p> : (<>
            <div className="form-row">
              <div className="form-group"><label>Seitentitel (Browser-Tab)</label><input className="form-input" value={cfg.page_title} onChange={set('page_title')} data-testid="input-page-title" /></div>
              <div className="form-group"><label>Hauptüberschrift</label><input className="form-input" value={cfg.heading} onChange={set('heading')} data-testid="input-heading" /></div>
            </div>
            <div className="form-group"><label>Beschreibungstext</label><textarea className="form-input" rows={3} value={cfg.description} onChange={set('description')} data-testid="input-description" /></div>
            <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>Suchformular</h4>
            <div className="form-row">
              <div className="form-group"><label>Label &bdquo;Standort&ldquo;</label><input className="form-input" value={cfg.label_location} onChange={set('label_location')} data-testid="input-label-location" /></div>
              <div className="form-group"><label>Label &bdquo;Umkreis&ldquo;</label><input className="form-input" value={cfg.label_radius} onChange={set('label_radius')} data-testid="input-label-radius" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Label &bdquo;Angebot&ldquo;</label><input className="form-input" value={cfg.label_offer} onChange={set('label_offer')} data-testid="input-label-offer" /></div>
              <div className="form-group"><label>Platzhalter im Standort-Feld</label><input className="form-input" value={cfg.placeholder_location} onChange={set('placeholder_location')} data-testid="input-placeholder" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Button-Text &bdquo;Suchen&ldquo;</label><input className="form-input" value={cfg.button_search} onChange={set('button_search')} data-testid="input-btn-search" /></div>
              <div className="form-group">
                <label>Ergebnistext</label>
                <input className="form-input" value={cfg.results_text} onChange={set('results_text')} data-testid="input-results-text" />
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}><code style={{ background: 'var(--page-bg)', padding: '2px 6px', borderRadius: 4 }}>{'{count}'}</code> = Anzahl der Ergebnisse</p>
              </div>
            </div>
            <div className="form-group"><label>Meldung &bdquo;Keine Ergebnisse&ldquo;</label><input className="form-input" value={cfg.no_results} onChange={set('no_results')} data-testid="input-no-results" /></div>
          </>)}
        </div>
      </div>

      {/* ── Texte Detailseite ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-detail-texts-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(59,130,246,0.1)', color: 'var(--info)' }}><Type size={18} /></span> Texte der Detailseite</h3>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group"><label>Zurück-Button</label><input className="form-input" value={cfg.detail_back_button} onChange={set('detail_back_button')} data-testid="input-detail-back" /></div>
            <div className="form-group"><label>Drucken-Button</label><input className="form-input" value={cfg.detail_print_button} onChange={set('detail_print_button')} data-testid="input-detail-print" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Überschrift &bdquo;Kontaktinformationen&ldquo;</label><input className="form-input" value={cfg.detail_contact_heading} onChange={set('detail_contact_heading')} data-testid="input-detail-contact" /></div>
            <div className="form-group"><label>Überschrift &bdquo;Angebot&ldquo;</label><input className="form-input" value={cfg.detail_offers_heading} onChange={set('detail_offers_heading')} data-testid="input-detail-offers" /></div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-footer-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(107,114,128,0.1)', color: '#6B7280' }}><Globe size={18} /></span> Footer</h3>
        </div>
        <div className="card-body">
          <div className="form-group"><label>Footer-Links (HTML)</label><textarea className="form-input" rows={2} value={cfg.footer_links_html} onChange={set('footer_links_html')} data-testid="input-footer-links" /></div>
          <div className="form-group">
            <label>Copyright-Text</label>
            <input className="form-input" value={cfg.footer_copyright} onChange={set('footer_copyright')} data-testid="input-footer-copyright" />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}><code style={{ background: 'var(--page-bg)', padding: '2px 6px', borderRadius: 4 }}>{'{year}'}</code> = aktuelles Jahr</p>
          </div>
        </div>
      </div>

      {/* ── Widget-Einstellungen ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-widget-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366F1' }}><Code size={18} /></span> Widget (Einbettung ohne iframe)</h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Betten Sie die Herzgruppensuche direkt auf Ihrer Website ein — ohne iframe. Das Widget wird als eigenständiges JavaScript geladen und rendert die komplette Suche in einem <code style={{ background: 'var(--page-bg)', padding: '2px 6px', borderRadius: 4 }}>&lt;div&gt;</code> auf Ihrer Seite.
          </p>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Base-URL (Wo liegt die Herzgruppensuche auf Ihrem Server?)</label>
            <input className="form-input" value={cfg.widget_base_url || ''} onChange={set('widget_base_url')}
              placeholder="z.B. https://herzgruppen-berlin.de/herzgruppensuche"
              data-testid="widget-base-url-input" />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Die URL zum Verzeichnis, in das die Herzgruppensuche hochgeladen wurde (ohne abschließenden Schrägstrich).</p>
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              onClick={() => setCfg(prev => ({ ...prev, widget_show_logo: !prev.widget_show_logo }))}
              data-testid="widget-toggle-logo">
              {cfg.widget_show_logo ? <ToggleRight size={28} color="#E6007E" /> : <ToggleLeft size={28} color="#9CA3AF" />}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Logo/Header anzeigen</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Zeigt das BGPR-Logo im Widget an</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
              onClick={() => setCfg(prev => ({ ...prev, widget_show_footer: !prev.widget_show_footer }))}
              data-testid="widget-toggle-footer">
              {cfg.widget_show_footer ? <ToggleRight size={28} color="#E6007E" /> : <ToggleLeft size={28} color="#9CA3AF" />}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Footer/Impressum anzeigen</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Zeigt den Footer mit Links und Copyright im Widget an</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Überschrift-Farbe</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={cfg.widget_heading_color || '#2c3e50'}
                  onChange={e => setCfg(prev => ({ ...prev, widget_heading_color: e.target.value }))}
                  style={{ width: 40, height: 32, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: 0 }}
                  data-testid="widget-heading-color" />
                <input className="form-input" value={cfg.widget_heading_color || '#2c3e50'}
                  onChange={set('widget_heading_color')}
                  style={{ width: 100, fontFamily: 'monospace', fontSize: 13 }}
                  data-testid="widget-heading-color-text" />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Titel & Labels</span>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Einleitungstext-Farbe</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={cfg.widget_text_color || '#555555'}
                  onChange={e => setCfg(prev => ({ ...prev, widget_text_color: e.target.value }))}
                  style={{ width: 40, height: 32, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: 0 }}
                  data-testid="widget-text-color" />
                <input className="form-input" value={cfg.widget_text_color || '#555555'}
                  onChange={set('widget_text_color')}
                  style={{ width: 100, fontFamily: 'monospace', fontSize: 13 }}
                  data-testid="widget-text-color-text" />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Beschreibungstext</span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Embed-Code zum Kopieren</label>
            <div style={{ position: 'relative' }}>
              <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '16px 16px 16px 16px', borderRadius: 8, fontSize: 13, fontFamily: "'JetBrains Mono', 'Fira Code', monospace", lineHeight: 1.6, overflowX: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }} data-testid="widget-embed-code">
{`<script src="${widgetBaseUrl || '[IHRE-BASE-URL]'}/widget.js"><\/script>\n<div id="bgpr-herzgruppensuche"></div>`}
              </pre>
              <button onClick={handleCopyEmbed}
                style={{ position: 'absolute', top: 8, right: 8, background: embedCopied ? '#10B981' : '#475569', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, transition: 'background 0.2s' }}
                data-testid="widget-copy-btn">
                {embedCopied ? <><Check size={12} /> Kopiert!</> : <><Copy size={12} /> Kopieren</>}
              </button>
            </div>
            {!widgetBaseUrl && (
              <p style={{ fontSize: 11, color: '#F59E0B', marginTop: 6 }}>Bitte geben Sie zuerst die Base-URL ein, damit der Embed-Code korrekt generiert wird.</p>
            )}
          </div>

          {/* Widget action buttons */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleWidgetSave} disabled={widgetSaving} data-testid="widget-save-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {widgetSaving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {widgetSaving ? 'Speichern...' : 'Widget-Einstellungen speichern'}
            </button>
            <button className="btn btn-secondary" onClick={() => {
                if (!widgetLivePreview) {
                  handleWidgetSave().then(() => setWidgetLivePreview(true));
                } else {
                  setWidgetLivePreview(false);
                }
              }}
              data-testid="widget-live-preview-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Eye size={14} /> {widgetLivePreview ? 'Live-Vorschau ausblenden' : 'Live-Vorschau einblenden'}
            </button>
            <button className="btn" onClick={() => { handleWidgetSave().then(() => setShowWidgetPreview(true)); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6366F1', border: '1px solid #A5B4FC', background: '#EEF2FF' }}
              data-testid="widget-fullscreen-btn">
              <ExternalLink size={14} /> Vollbild-Vorschau
            </button>
          </div>

          {/* Inline Live Preview */}
          {widgetLivePreview && (
            <div style={{ marginTop: 20 }} data-testid="widget-live-preview-area">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Live-Vorschau</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Speichern Sie Änderungen, um die Vorschau zu aktualisieren</span>
              </div>
              <div style={{ border: '2px solid #E6007E22', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                <iframe
                  key={widgetPreviewKey}
                  src={widgetPreviewUrl}
                  title="Widget Live-Vorschau"
                  style={{ width: '100%', height: 700, border: 'none', display: 'block' }}
                  data-testid="widget-live-preview-iframe"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Widget Fullscreen Preview Modal ── */}
      {showWidgetPreview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowWidgetPreview(false)}>
          <div style={{ width: '90vw', height: '85vh', background: '#fff', borderRadius: 12, overflow: 'hidden', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #eee', background: '#f9fafb' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Widget-Vorschau — Herzgruppensuche</span>
              <button onClick={() => setShowWidgetPreview(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} data-testid="widget-preview-close"><X size={20} /></button>
            </div>
            <iframe key={'fullscreen-' + widgetPreviewKey} src={widgetPreviewUrl} title="Widget Vorschau" style={{ width: '100%', height: 'calc(100% - 46px)', border: 'none' }} data-testid="widget-preview-iframe" />
          </div>
        </div>
      )}

      {/* ── FTP/SFTP Konfiguration ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-ftp-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--success)' }}><Server size={18} /></span> FTP/SFTP-Zugangsdaten</h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Konfigurieren Sie die Zugangsdaten für den Upload der Herzgruppensuche auf Ihren Webserver.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Protokoll</label>
              <select className="form-input" value={ftpCfg.protocol} onChange={setFtp('protocol')} data-testid="ftp-protocol">
                <option value="sftp">SFTP (empfohlen)</option>
                <option value="ftp">FTP</option>
              </select>
            </div>
            <div className="form-group">
              <label>Host</label>
              <input className="form-input" value={ftpCfg.host} onChange={setFtp('host')} placeholder="z.B. ftp.meinserver.de" data-testid="ftp-host" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Port</label>
              <input className="form-input" value={ftpCfg.port} onChange={setFtp('port')} placeholder={ftpCfg.protocol === 'sftp' ? '22' : '21'} style={{ maxWidth: 100 }} data-testid="ftp-port" />
            </div>
            <div className="form-group">
              <label>Benutzer</label>
              <input className="form-input" value={ftpCfg.user} onChange={setFtp('user')} placeholder="FTP-Benutzername" data-testid="ftp-user" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Passwort {ftpPasswordSet && <span style={{ fontSize: 11, color: 'var(--success)', marginLeft: 6 }}><ShieldCheck size={12} style={{ verticalAlign: -2 }} /> gespeichert</span>}</label>
              <input className="form-input" type="password" value={ftpCfg.password} onChange={setFtp('password')} placeholder={ftpPasswordSet ? '(unverändert lassen oder neues eingeben)' : 'FTP-Passwort'} data-testid="ftp-password" />
            </div>
            <div className="form-group">
              <label>Remote-Pfad</label>
              <input className="form-input" value={ftpCfg.remote_path} onChange={setFtp('remote_path')} placeholder="/httpdocs/herzgruppensuche" data-testid="ftp-path" />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-primary" onClick={handleFtpSave} disabled={ftpSaving} data-testid="ftp-save-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {ftpSaving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {ftpSaving ? 'Speichern...' : 'FTP-Daten speichern'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Actions: Save + Preview ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-secondary" onClick={() => { handleSave().then(() => setShowPreview(true)); }} data-testid="hgs-preview-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Eye size={14} /> Vorschau
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="hgs-save-btn">
          {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>

      {/* ── Versionen erstellen ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="hgs-create-version-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--success)' }}><FileArchive size={18} /></span> Neue Version erstellen & herunterladen</h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Erstellen Sie eine neue Version der Herzgruppensuche mit den aktuellen Einstellungen. Die ZIP-Datei wird gespeichert und kann jederzeit erneut heruntergeladen werden.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Optionale Notiz zur Version</label>
              <input className="form-input" placeholder="z.B. Neue Farben, aktualisierte Texte..." value={versionNote} onChange={e => setVersionNote(e.target.value)} data-testid="version-note-input" />
            </div>
            <button className="btn btn-primary" onClick={handleCreateVersion} disabled={creating} data-testid="hgs-create-version-btn" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {creating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />}
              {creating ? 'Wird erstellt...' : 'Version erstellen & herunterladen'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Versionshistorie ── */}
      <div className="card" style={{ marginBottom: '2rem' }} data-testid="hgs-versions-section">
        <div className="card-header">
          <h3><span className="card-header-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}><History size={18} /></span> Versionshistorie ({versions.length})</h3>
        </div>
        <div className="card-body">
          {!versions.length ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Noch keine Versionen erstellt.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="versions-table">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thS}>Version</th>
                  <th style={thS}>Datum</th>
                  <th style={thS}>Erstellt von</th>
                  <th style={thS}>Notiz</th>
                  <th style={thS}>Größe</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <tr key={v.version} style={{ borderBottom: '1px solid var(--border)' }} data-testid={`version-row-${v.version}`}>
                    <td style={tdS}><strong>v{v.version}</strong></td>
                    <td style={tdS}>{formatDate(v.created_at)}</td>
                    <td style={tdS}>{v.created_by || '–'}</td>
                    <td style={{ ...tdS, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.note || '–'}</td>
                    <td style={tdS}>{formatBytes(v.file_size)}</td>
                    <td style={{ ...tdS, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {v.file_exists ? (<>
                        <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                          onClick={() => downloadVersion(v.version, v.filename)} data-testid={`download-version-${v.version}`}>
                          <Download size={12} /> Download
                        </button>
                        <button className="btn" style={{ padding: '4px 10px', fontSize: 12, marginRight: 6, color: '#3B82F6', border: '1px solid #93C5FD', background: '#EFF6FF' }}
                          onClick={() => handleFtpUpload(v.version)} disabled={uploadingVersion === v.version || !ftpCfg.host}
                          title={!ftpCfg.host ? 'FTP-Zugangsdaten nicht konfiguriert' : 'Auf Server hochladen'}
                          data-testid={`ftp-upload-version-${v.version}`}>
                          {uploadingVersion === v.version ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={12} />}
                          {uploadingVersion === v.version ? ' Hochladen...' : ' Server'}
                        </button>
                      </>) : (
                        <span style={{ fontSize: 11, color: '#EF4444', marginRight: 6 }}>Datei fehlt</span>
                      )}
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: '#EF4444', border: '1px solid #FCA5A5', background: '#FEF2F2' }}
                        onClick={() => deleteVersion(v.version)} data-testid={`delete-version-${v.version}`}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const thS = { textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
const tdS = { padding: '10px 12px', verticalAlign: 'middle' };
